/**
 * Native Channel-Provider Integration Catalog
 * ===========================================
 * One declarative manifest per provider. The Integrations UI renders a form
 * from `fields`, and on save assembles `api_config` and POSTs to the SAME
 * backend the admin uses:
 *
 *   POST /channel_api_config/create
 *   { customer_id (FROM JWT), channel_id, account_name, api_config, status_id }
 *
 * So NO backend/model change is required — `tbl_channel_api_config.api_config`
 * is already a free-form JSON blob. The only differences from the admin form:
 *   1. customer_id is pinned to the logged-in tenant (no "All Customers" picker).
 *   2. The customer enters only credentials; the request mapping is baked in here.
 *
 * Each provider declares a `delivery` strategy:
 *   - 'http_template'  → api_config carries a {url, method, headers, payload}
 *                        template (with {{field}} interpolation). Runs through
 *                        the EXISTING generic HTTP sender — plug-and-play, no
 *                        backend adapter needed. (Same shape as the admin SMS form.)
 *   - 'backend_adapter'→ api_config carries raw credentials; a server-side gateway
 *                        adapter (xPush) signs/sends. Needed for SDK/SigV4/JWT APIs
 *                        (AWS SES, FCM, APNs, SMTP) that can't be a pure HTTP template.
 *   - 'oauth'          → no manual fields; a hosted OAuth/Embedded-Signup flow returns
 *                        the credentials (WhatsApp / Meta).
 *
 * `type` drives the UI: 'credential' (form) · 'oauth' (Connect button) · 'custom' (raw HTTP).
 */

import eoceanLogo from './logos/eocean.svg';
import { BRAND_NAME } from '../../theme/brand';

export const FIELD = {
  TEXT: 'text', PASSWORD: 'password', NUMBER: 'number', EMAIL: 'email',
  PHONE: 'tel', SELECT: 'select', TEXTAREA: 'textarea', FILE: 'file', SWITCH: 'switch',
};

/** eOcean ships as a bundled SVG (custom mark). Brand logos render from react-icons/si
 *  (bundled — no external calls). See BRAND_ICONS in Integrations.jsx. */
export const LOGOS = {
  eocean_sms: eoceanLogo,
  eocean_ivr: eoceanLogo,
};
export const logoFor = (id) => LOGOS[id] || null;

/** Channel definitions (channel_id is resolved from the platform's channel list at runtime). */
export const CHANNELS = [
  { slug: 'whatsapp', channelId: 2, name: 'WhatsApp',    icon: 'whatsapp', tagline: 'Rich messaging via WhatsApp Business' },
  { slug: 'sms',      channelId: 1, name: 'SMS',         icon: 'message',  tagline: 'Transactional & promotional text' },
  { slug: 'email',    channelId: 3, name: 'Email',       icon: 'mail',     tagline: 'Marketing & transactional email' },
  { slug: 'voice',    channelId: 4, name: 'Voice / IVR', icon: 'phone',    tagline: 'Automated voice calls & IVR' },
  { slug: 'push',     channelId: 6, name: 'Push',        icon: 'bell',     tagline: 'Mobile push notifications' },
];

/** slug ⇆ tbl_msg_channel.id (1 SMS · 2 WhatsApp · 3 Email · 4 Voice · 5 In-App · 6 Push · 7 SMS-Secondary). */
export const channelIdOf = (slug) => (CHANNELS.find((c) => c.slug === slug) || {}).channelId || null;

export const PROVIDERS = [
  // ─────────────────────────────────────────────────────────── SMS ───────────
  {
    id: 'eocean_sms', channel: 'sms', name: 'eOcean',
    tagline: 'Transactional & promotional SMS via eOcean',
    native: true, recommended: true, type: 'credential', delivery: 'http_template',
    logoText: 'eO', logoBg: '#4f7cf7', docs: '',
    note: 'House provider (parent company). TODO: confirm endpoint + payload against the eOcean SMS API spec.',
    fields: [
      { name: 'api_key',   label: 'API Key',          type: FIELD.PASSWORD, required: true, secret: true, placeholder: 'eOcean API key' },
      { name: 'sender_id', label: 'Sender ID / Mask',  type: FIELD.TEXT,     required: true, placeholder: `e.g. ${BRAND_NAME}`, help: 'The approved alphanumeric mask or short code.' },
      { name: 'username',  label: 'Username',          type: FIELD.TEXT,     required: false, placeholder: 'If your account uses user/pass' },
      { name: 'password',  label: 'Password',          type: FIELD.PASSWORD, required: false, secret: true },
    ],
    request_template: { // TODO: replace url/payload with the exact eOcean spec
      url: 'https://api.eocean.us/sms/send', method: 'Post',
      headers: { Authorization: 'Bearer {{api_key}}', 'Content-Type': 'application/json' },
      payload: { mask: '{{sender_id}}', to: '{{to}}', message: '{{message}}' },
    },
    test: { kind: 'send_sms', help: 'Sends a test SMS to a number you provide.' },
  },
  {
    id: 'twilio_sms', channel: 'sms', name: 'Twilio',
    tagline: 'Transactional & promotional SMS via Twilio',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'TW', logoBg: '#f22f46', docs: 'https://www.twilio.com/docs/sms',
    fields: [
      { name: 'account_sid', label: 'Account SID',  type: FIELD.TEXT,     required: true, placeholder: 'ACxxxxxxxx' },
      { name: 'auth_token',  label: 'Auth Token',   type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'from_number', label: 'From Number',  type: FIELD.PHONE,    required: true, placeholder: '+1xxxxxxxxxx', help: 'Your Twilio sending number (E.164).' },
    ],
    request_template: {
      url: 'https://api.twilio.com/2010-04-01/Accounts/{{account_sid}}/Messages.json', method: 'Post',
      auth: { type: 'basic', user: '{{account_sid}}', pass: '{{auth_token}}' },
      encoding: 'form',
      payload: { From: '{{from_number}}', To: '{{to}}', Body: '{{message}}' },
    },
    test: { kind: 'send_sms' },
  },
  {
    id: 'messagebird', channel: 'sms', name: 'MessageBird',
    tagline: 'Global SMS via MessageBird (Bird)',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'MB', logoBg: '#2481d7', docs: 'https://developers.messagebird.com/api/sms-messaging/',
    fields: [
      { name: 'api_key',   label: 'API Key',    type: FIELD.PASSWORD, required: true, secret: true, placeholder: 'live_xxxxxxxx' },
      { name: 'sender_id', label: 'Originator', type: FIELD.TEXT,     required: true, placeholder: `e.g. ${BRAND_NAME} or +1xxxxxxxxxx`, help: 'Alphanumeric sender or number.' },
    ],
    request_template: { // MessageBird REST is form-encoded (per official docs), not JSON
      url: 'https://rest.messagebird.com/messages', method: 'Post', encoding: 'form',
      headers: { Authorization: 'AccessKey {{api_key}}' },
      payload: { originator: '{{sender_id}}', recipients: '{{to}}', body: '{{message}}' },
    },
    test: { kind: 'send_sms' },
  },
  {
    id: 'sinch', channel: 'sms', name: 'Sinch',
    tagline: 'Global SMS via Sinch',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'Si', logoBg: '#e0001a', docs: 'https://developers.sinch.com/docs/sms/api-reference/',
    fields: [
      { name: 'service_plan_id', label: 'Service Plan ID', type: FIELD.TEXT,     required: true },
      { name: 'api_token',       label: 'API Token',       type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'from_number',     label: 'From Number',     type: FIELD.PHONE,    required: true, placeholder: '+1xxxxxxxxxx' },
    ],
    request_template: {
      url: 'https://sms.api.sinch.com/xms/v1/{{service_plan_id}}/batches', method: 'Post',
      headers: { Authorization: 'Bearer {{api_token}}', 'Content-Type': 'application/json' },
      payload: { from: '{{from_number}}', to: ['{{to}}'], body: '{{message}}' },
    },
    test: { kind: 'send_sms' },
  },
  {
    id: 'infobip', channel: 'sms', name: 'Infobip',
    tagline: 'Global enterprise SMS via Infobip',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'IB', logoBg: '#ff6900', docs: 'https://www.infobip.com/docs/api/channels/sms',
    fields: [
      { name: 'base_url',  label: 'Base URL',  type: FIELD.TEXT,     required: true, placeholder: 'xxxxx.api.infobip.com', help: 'Your account base URL (no https://).' },
      { name: 'api_key',   label: 'API Key',   type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'sender_id', label: 'Sender ID', type: FIELD.TEXT,     required: true, placeholder: `e.g. ${BRAND_NAME}` },
    ],
    request_template: {
      url: 'https://{{base_url}}/sms/2/text/advanced', method: 'Post',
      headers: { Authorization: 'App {{api_key}}', 'Content-Type': 'application/json', Accept: 'application/json' },
      payload: { messages: [{ from: '{{sender_id}}', destinations: [{ to: '{{to}}' }], text: '{{message}}' }] },
    },
    test: { kind: 'send_sms' },
  },
  {
    id: 'aws_sns', channel: 'sms', name: 'AWS SNS',
    tagline: 'SMS via Amazon SNS',
    native: true, type: 'credential', delivery: 'backend_adapter',
    logoText: 'SNS', logoBg: '#ff9900', docs: 'https://docs.aws.amazon.com/sns/latest/dg/sms_publish-to-phone.html',
    note: 'SNS uses SigV4 signing → handled by a backend gateway adapter, not a raw HTTP template.',
    fields: [
      { name: 'access_key_id',     label: 'Access Key ID',     type: FIELD.TEXT,     required: true },
      { name: 'secret_access_key', label: 'Secret Access Key', type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'region',            label: 'Region',            type: FIELD.SELECT,   required: true, options: ['us-east-1','us-west-2','eu-west-1','ap-south-1','ap-southeast-1'] },
      { name: 'sender_id',         label: 'Sender ID',         type: FIELD.TEXT,     required: false, help: 'Where the destination country supports it.' },
    ],
    test: { kind: 'send_sms' },
  },
  {
    id: 'plivo', channel: 'sms', name: 'Plivo',
    tagline: 'SMS & Voice via Plivo',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'PL', logoBg: '#1b61d1', docs: 'https://www.plivo.com/docs/sms/api/message/',
    fields: [
      { name: 'auth_id',     label: 'Auth ID',     type: FIELD.TEXT,     required: true },
      { name: 'auth_token',  label: 'Auth Token',  type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'from_number', label: 'From Number', type: FIELD.PHONE,    required: true, placeholder: '+1xxxxxxxxxx' },
    ],
    request_template: {
      url: 'https://api.plivo.com/v1/Account/{{auth_id}}/Message/', method: 'Post',
      auth: { type: 'basic', user: '{{auth_id}}', pass: '{{auth_token}}' },
      payload: { src: '{{from_number}}', dst: '{{to}}', text: '{{message}}' },
    },
    test: { kind: 'send_sms' },
  },
  {
    id: 'custom_sms', channel: 'sms', name: 'Custom Provider',
    tagline: 'Any SMS gateway via a raw HTTP request',
    type: 'custom', delivery: 'http_template', logoText: '{ }', logoBg: '#64748b',
    help: 'For gateways not in the catalog — map the endpoint, headers, and payload yourself. (This is the existing admin SMS form.)',
    fields: 'custom_http', // renders the url/method/headers[]/payload[] builder
    test: { kind: 'send_sms' },
  },

  // ────────────────────────────────────────────────────── WhatsApp ───────────
  {
    id: 'whatsapp_meta', channel: 'whatsapp', name: 'WhatsApp (Meta)',
    tagline: 'Connect your WhatsApp Business number in one click',
    native: true, recommended: true, type: 'oauth', delivery: 'oauth',
    logoText: 'WA', logoBg: '#25d366',
    docs: 'https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/overview',
    oauth: {
      provider: 'meta',
      sdk: 'facebook-js',                 // load https://connect.facebook.net/en_US/sdk.js
      app_id: 'ENV:REACT_APP_META_APP_ID',
      config_id: 'ENV:REACT_APP_META_ES_CONFIG_ID', // Embedded Signup configuration id
      scopes: ['whatsapp_business_management', 'whatsapp_business_messaging', 'business_management'],
      // Backend exchanges the returned `code` for a system-user token and reads waba_id + phone_number_id.
      exchange_endpoint: '/integrations/whatsapp/embedded-signup/exchange',
      // Stored into api_config (same keys the admin Meta form stores):
      returns: ['waba_id', 'business_number', 'token', 'app_id', 'business_name'],
      post_steps: ['subscribe_app_to_waba_webhooks', 'register_phone_number'],
    },
    // Embedded Signup only — no manual WABA/token entry.
    test: { kind: 'send_whatsapp_template' },
  },

  // ───────────────────────────────────────────────────────── Email ───────────
  {
    id: 'sendgrid', channel: 'email', name: 'SendGrid',
    tagline: 'Marketing & transactional email via SendGrid',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'SG', logoBg: '#1a82e2', docs: 'https://docs.sendgrid.com/api-reference/mail-send/mail-send',
    fields: [
      { name: 'api_key',      label: 'API Key',     type: FIELD.PASSWORD, required: true, secret: true, placeholder: 'SG.xxxxxxxx' },
      { name: 'from_address', label: 'From Email',  type: FIELD.EMAIL,    required: true, placeholder: 'noreply@yourdomain.com' },
      { name: 'from_name',    label: 'From Name',   type: FIELD.TEXT,     required: true },
    ],
    request_template: {
      url: 'https://api.sendgrid.com/v3/mail/send', method: 'Post',
      headers: { Authorization: 'Bearer {{api_key}}', 'Content-Type': 'application/json' },
      payload: {
        personalizations: [{ to: [{ email: '{{to}}' }], subject: '{{subject}}' }],
        from: { email: '{{from_address}}', name: '{{from_name}}' },
        content: [{ type: 'text/html', value: '{{message}}' }],
      },
    },
    test: { kind: 'send_email' },
  },
  {
    id: 'aws_ses', channel: 'email', name: 'AWS SES',
    tagline: 'High-deliverability email via Amazon SES',
    native: true, type: 'credential', delivery: 'backend_adapter',
    logoText: 'SES', logoBg: '#ff9900', docs: 'https://docs.aws.amazon.com/ses/',
    note: 'SES uses SigV4 signing → handled by a backend gateway adapter, not a raw HTTP template.',
    fields: [
      { name: 'access_key_id',     label: 'Access Key ID',     type: FIELD.TEXT,     required: true },
      { name: 'secret_access_key', label: 'Secret Access Key', type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'region',            label: 'Region',            type: FIELD.SELECT,   required: true, options: ['us-east-1','us-west-2','eu-west-1','ap-south-1','ap-southeast-1'] },
      { name: 'from_address',      label: 'From Email',        type: FIELD.EMAIL,    required: true, help: 'Must be a verified SES identity.' },
      { name: 'from_name',         label: 'From Name',         type: FIELD.TEXT,     required: true },
    ],
    test: { kind: 'send_email' },
  },
  {
    id: 'brevo', channel: 'email', name: 'Brevo',
    tagline: 'Transactional & marketing email via Brevo (Sendinblue)',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'BR', logoBg: '#0b996e', docs: 'https://developers.brevo.com/reference/sendtransacemail',
    fields: [
      { name: 'api_key',      label: 'API Key (v3)', type: FIELD.PASSWORD, required: true, secret: true, placeholder: 'xkeysib-xxxxxxxx' },
      { name: 'from_address', label: 'From Email',    type: FIELD.EMAIL,    required: true, placeholder: 'noreply@yourdomain.com' },
      { name: 'from_name',    label: 'From Name',     type: FIELD.TEXT,     required: true },
    ],
    request_template: {
      url: 'https://api.brevo.com/v3/smtp/email', method: 'Post',
      headers: { 'api-key': '{{api_key}}', 'Content-Type': 'application/json', accept: 'application/json' },
      payload: {
        sender: { email: '{{from_address}}', name: '{{from_name}}' },
        to: [{ email: '{{to}}' }],
        subject: '{{subject}}', htmlContent: '{{message}}',
      },
    },
    test: { kind: 'send_email' },
  },
  {
    id: 'klaviyo', channel: 'email', name: 'Klaviyo',
    tagline: 'Email marketing automation via Klaviyo',
    native: true, type: 'credential', delivery: 'backend_adapter',
    logoText: 'KL', logoBg: '#000000', docs: 'https://developers.klaviyo.com/en/reference/api_overview',
    note: 'Klaviyo sends through events → flows/campaigns; a backend adapter relays them via the Klaviyo API (not a single transactional template).',
    fields: [
      { name: 'api_key',      label: 'Private API Key', type: FIELD.PASSWORD, required: true, secret: true, placeholder: 'pk_xxxxxxxx' },
      { name: 'from_name',    label: 'From Name',       type: FIELD.TEXT,     required: false, help: 'Sender identity is usually configured inside Klaviyo.' },
      { name: 'from_address', label: 'From Email',      type: FIELD.EMAIL,    required: false, placeholder: 'noreply@yourdomain.com' },
    ],
    test: { kind: 'send_email' },
  },
  {
    id: 'custom_smtp', channel: 'email', name: 'Custom SMTP',
    tagline: 'Marketing & transactional email via your own SMTP server',
    native: true, type: 'credential', delivery: 'backend_adapter',
    logoText: '@', logoBg: '#64748b',
    fields: [ // standard SMTP server config (host/port/credentials + sender)
      { name: 'host',         label: 'Host',                 type: FIELD.TEXT,     required: true,  placeholder: 'smtp.yourhost.com' },
      { name: 'port',         label: 'Port',                 type: FIELD.NUMBER,   required: true,  placeholder: '587' },
      { name: 'username',     label: 'Username',             type: FIELD.TEXT,     required: true },
      { name: 'password',     label: 'Password',             type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'from_name',    label: 'From Name',            type: FIELD.TEXT,     required: true },
      { name: 'from_address', label: 'Sending Email Address',type: FIELD.EMAIL,    required: true,  placeholder: 'noreply@yourdomain.com' },
    ],
    test: { kind: 'send_email' },
  },

  // ─────────────────────────────────────────────────── Voice / IVR ───────────
  {
    id: 'eocean_ivr', channel: 'voice', name: 'eOcean IVR',
    tagline: 'Automated voice calls & IVR via eOcean',
    native: true, recommended: true, type: 'credential', delivery: 'http_template',
    logoText: 'eO', logoBg: '#4f7cf7',
    note: 'House provider (parent company). TODO: confirm endpoint + payload against the eOcean IVR API spec.',
    fields: [
      { name: 'api_key',     label: 'API Key',           type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'caller_id',   label: 'Caller ID / Short Code', type: FIELD.TEXT, required: true, placeholder: 'e.g. 3000' },
      { name: 'campaign_id', label: 'Campaign / Service ID',  type: FIELD.TEXT, required: false, help: 'If eOcean scopes IVR by campaign.' },
    ],
    request_template: { // TODO: confirm against eOcean IVR spec
      url: 'https://api.eocean.us/ivr/call', method: 'Post',
      headers: { Authorization: 'Bearer {{api_key}}', 'Content-Type': 'application/json' },
      payload: { caller_id: '{{caller_id}}', to: '{{to}}', audio_url: '{{audio_url}}' },
    },
    test: { kind: 'place_call' },
  },
  {
    id: 'twilio_voice', channel: 'voice', name: 'Twilio Voice',
    tagline: 'Programmable voice calls via Twilio',
    native: true, type: 'credential', delivery: 'http_template',
    logoText: 'TW', logoBg: '#f22f46', docs: 'https://www.twilio.com/docs/voice',
    fields: [
      { name: 'account_sid', label: 'Account SID',  type: FIELD.TEXT,     required: true },
      { name: 'auth_token',  label: 'Auth Token',   type: FIELD.PASSWORD, required: true, secret: true },
      { name: 'from_number', label: 'From Number',  type: FIELD.PHONE,    required: true, placeholder: '+1xxxxxxxxxx' },
      { name: 'twiml_url',   label: 'TwiML / Voice URL', type: FIELD.TEXT, required: false, help: 'Webhook that returns call instructions.' },
    ],
    request_template: {
      url: 'https://api.twilio.com/2010-04-01/Accounts/{{account_sid}}/Calls.json', method: 'Post',
      auth: { type: 'basic', user: '{{account_sid}}', pass: '{{auth_token}}' },
      encoding: 'form',
      payload: { From: '{{from_number}}', To: '{{to}}', Url: '{{twiml_url}}' },
    },
    test: { kind: 'place_call' },
  },

  // ────────────────────────────────────────────────────────── Push ───────────
  {
    id: 'xpush', channel: 'push', name: 'xPush',
    tagline: `Native push & in-app messaging by ${BRAND_NAME} — no FCM/APNs account to wire`,
    native: true, recommended: true, type: 'credential', delivery: 'backend_adapter',
    logoText: 'xP', logoBg: 'var(--brand-primary)',
    note: `${BRAND_NAME} built-in push + in-app/realtime service. Create an xPush app to get a Property ID + SDK Key; xPush handles cross-platform delivery.`,
    fields: [
      { name: 'property_id', label: 'Property ID', type: FIELD.TEXT,     required: true, placeholder: 'xPush app / property id', help: `Issued by ${BRAND_NAME} when you create an xPush app.` },
      { name: 'sdk_key',     label: 'SDK Key',     type: FIELD.PASSWORD, required: true, secret: true, help: 'The xPush SDK key for this app.' },
    ],
    test: { kind: 'send_push' },
  },
  {
    id: 'fcm', channel: 'push', name: 'Firebase (FCM)',
    tagline: 'Android & web push via Firebase Cloud Messaging',
    native: true, type: 'credential', delivery: 'backend_adapter',
    logoText: 'FCM', logoBg: '#ffca28', docs: 'https://firebase.google.com/docs/cloud-messaging',
    note: 'FCM v1 needs a service-account OAuth (JWT) → backend gateway adapter.',
    fields: [
      { name: 'project_id',          label: 'Firebase Project ID', type: FIELD.TEXT, required: true },
      { name: 'service_account_json',label: 'Service Account JSON', type: FIELD.FILE, required: true, secret: true, accept: '.json', help: 'Download from Firebase → Project Settings → Service Accounts.' },
    ],
    test: { kind: 'send_push' },
  },
  {
    id: 'apns', channel: 'push', name: 'Apple (APNs)',
    tagline: 'iOS push via Apple Push Notification service',
    native: true, type: 'credential', delivery: 'backend_adapter',
    logoText: '', logoBg: '#000000', docs: 'https://developer.apple.com/documentation/usernotifications',
    note: 'APNs token auth needs .p8 JWT signing → backend gateway adapter.',
    fields: [
      { name: 'team_id',   label: 'Apple Team ID',  type: FIELD.TEXT, required: true, placeholder: 'U43H3W6HX8' },
      { name: 'key_id',    label: 'Auth Key ID',    type: FIELD.TEXT, required: true, placeholder: '3RX6G38478' },
      { name: 'p8_key',    label: 'Auth Key (.p8)', type: FIELD.FILE, required: true, secret: true, accept: '.p8', help: 'APNs Auth Key from the Apple Developer portal.' },
      { name: 'bundle_id', label: 'App Bundle ID',  type: FIELD.TEXT, required: true, placeholder: 'co.yourapp.app' },
      { name: 'environment', label: 'Environment',  type: FIELD.SELECT, required: true, options: ['production','sandbox'] },
    ],
    test: { kind: 'send_push' },
  },
];

/** Providers for a channel, recommended/native first. */
export const providersByChannel = (slug) =>
  PROVIDERS.filter((p) => p.channel === slug)
    .sort((a, b) => (b.recommended ? 1 : 0) - (a.recommended ? 1 : 0) || (b.native ? 1 : 0) - (a.native ? 1 : 0));

export const getProvider = (id) => PROVIDERS.find((p) => p.id === id) || null;

/**
 * Assemble the api_config to POST to /channel_api_config/create.
 * - credential + backend_adapter → store the field values as-is (gateway adapter uses them).
 * - http_template               → store the {url, method, headers, payload} template so the
 *                                 generic sender runs it; runtime fills {{to}}/{{message}}/etc.
 * Secrets are never logged; the renderer should send them only over the create call.
 */
export function buildApiConfig(provider, values) {
  if (provider.type === 'custom') return values; // {url, method, header, payload} from the raw builder
  if (provider.delivery === 'http_template' && provider.request_template) {
    return { ...values, _template: provider.request_template };
  }
  return { ...values }; // backend_adapter: raw credentials
}

const catalog = { CHANNELS, PROVIDERS, providersByChannel, getProvider, buildApiConfig, FIELD };
export default catalog;
